import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/apiService';

const formatDateTime = (value) => {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return date.toLocaleString();
};

export default function SystemNotificationPanel({ limit = 5 }) {
    const { t } = useTranslation();
    const [notifications, setNotifications] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const loadNotifications = useCallback(async () => {
        setIsLoading(true);
        setError('');
        try {
            const data = await apiService.getMySystemNotifications();
            setNotifications(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to load system notifications', err);
            setError(err?.message || t('systemNotifications.loadReceivedFailed'));
        } finally {
            setIsLoading(false);
        }
    }, [t]);

    useEffect(() => {
        loadNotifications();
    }, [loadNotifications]);

    const handleMarkAsRead = useCallback(async (notificationId) => {
        try {
            const updated = await apiService.markSystemNotificationRead(notificationId);
            setNotifications(prev =>
                prev.map(item => item.id === notificationId ? { ...item, ...updated } : item)
            );
        } catch (err) {
            console.error('Failed to mark notification as read', err);
            setError(err?.message || t('systemNotifications.markAsReadFailed'));
        }
    }, [t]);

    const displayedNotifications = useMemo(() => {
        if (!limit || limit <= 0) {
            return notifications;
        }
        return notifications.slice(0, limit);
    }, [notifications, limit]);

    return (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl shadow-md p-6">
            <div className="flex items-start">
                <div className="flex-shrink-0">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                </div>
                <div className="ml-3 flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                        <h3 className="text-lg font-semibold text-gray-900">{t('updateNotifications')}</h3>
                        <div className="flex gap-2">
                            <button
                                onClick={loadNotifications}
                                className="px-3 py-1.5 text-xs font-semibold rounded-md border border-blue-200 text-blue-700 bg-white hover:bg-blue-50"
                                disabled={isLoading}
                            >
                                {t('systemNotifications.refresh')}
                            </button>
                        </div>
                    </div>

                    {error && (
                        <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                            {error}
                        </div>
                    )}

                    {isLoading ? (
                        <div className="space-y-3">
                            {[...Array(3)].map((_, index) => (
                                <div key={index} className="bg-white/80 rounded-lg p-4 animate-pulse">
                                    <div className="h-4 bg-slate-200 rounded w-1/3 mb-2"></div>
                                    <div className="h-3 bg-slate-200 rounded w-1/2 mb-2"></div>
                                    <div className="h-3 bg-slate-200 rounded w-3/4"></div>
                                </div>
                            ))}
                        </div>
                    ) : displayedNotifications.length === 0 ? (
                        <div className="text-sm text-gray-600 bg-white/60 border border-blue-100 rounded-lg px-4 py-6">
                            {t('systemNotifications.noReceived')}
                        </div>
                    ) : (
                        <div className="space-y-3 max-h-60 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-blue-300 scrollbar-track-blue-100">
                            {displayedNotifications.map(notification => {
                                const sentLabel = notification.sentTime
                                    ? t('systemNotifications.sentAt', { date: formatDateTime(notification.sentTime) })
                                    : t('systemNotifications.createdAt', { date: formatDateTime(notification.createdAt) });
                                const readLabel = notification.isRead && notification.readAt
                                    ? t('systemNotifications.readAt', { date: formatDateTime(notification.readAt) })
                                    : null;

                                return (
                                    <div key={notification.id} className="bg-white/80 border border-blue-100 rounded-lg p-4 shadow-sm">
                                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                                            <div>
                                                <div className="font-semibold text-gray-900">
                                                    {notification.title}
                                                </div>
                                                <div className="text-xs text-gray-500 mt-1 space-x-2">
                                                    <span>{sentLabel}</span>
                                                    {notification.createdByName && (
                                                        <span>× {notification.createdByName}</span>
                                                    )}
                                                    {readLabel && (
                                                        <span>{readLabel}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {notification.isRead ? (
                                                    <span className="px-2 py-1 text-xs font-semibold text-green-700 bg-green-100 rounded-full">
                                                        {t('systemNotifications.read')}
                                                    </span>
                                                ) : (
                                                    <>
                                                        <span className="px-2 py-1 text-xs font-semibold text-orange-700 bg-orange-100 rounded-full">
                                                            {t('systemNotifications.unread')}
                                                        </span>
                                                        <button
                                                            onClick={() => handleMarkAsRead(notification.id)}
                                                            className="px-2 py-1 text-xs font-semibold text-blue-700 border border-blue-200 rounded-md bg-white hover:bg-blue-50"
                                                        >
                                                            {t('systemNotifications.markAsRead')}
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <p className="mt-3 text-sm text-gray-700 whitespace-pre-line">
                                            {notification.content}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
